import { ArrowUpRight } from "@phosphor-icons/react";
import { motion } from "motion/react";
import { PageLayout } from "@/components/layout";

const posts = [
	{
		date: "Feb 7, 2026",
		tag: "Engineering",
		title: "Why we chose proof of PageRank over proof of stake",
		excerpt: "Existing consensus mechanisms optimize for the wrong thing. We needed something that rewards contribution, not capital. Here's what we built instead.",
		featured: true,
	},
	{
		date: "Jan 28, 2026",
		tag: "Company",
		title: "Introducing Basilica",
		excerpt: "After 18 months of building in stealth, we're ready to show you what we've been working on. A new foundation for public digital infrastructure.",
		featured: true,
	},
	{
		date: "Jan 15, 2026",
		tag: "Research",
		title: "Decentralized identity without the blockchain baggage",
		excerpt: "Identity on the internet is broken. Every platform holds your identity hostage. We built a system where you own your identity and carry it everywhere.",
	},
	{
		date: "Dec 20, 2025",
		tag: "Engineering",
		title: "Building a peer-to-peer CDN that actually works",
		excerpt: "Content delivery shouldn't depend on three companies. We share the technical details of our distributed content layer and the tradeoffs we made.",
	},
	{
		date: "Dec 5, 2025",
		tag: "Design",
		title: "Designing for permanence",
		excerpt: "How do you design software that's meant to outlast your company? Our approach to building interfaces for infrastructure that should last decades.",
	},
	{
		date: "Nov 18, 2025",
		tag: "Company",
		title: "Why Santa Cruz",
		excerpt: "We moved from SF to Santa Cruz. Best decision we made. Here's what changes when your team can surf before standup.",
	},
];

export default function BlogRoute() {
	const featured = posts.filter(p => p.featured);
	const rest = posts.filter(p => !p.featured);

	return (
		<PageLayout activeRoute="/blog">
			{/* Hero */}
			<section className="pt-20 md:pt-32 pb-16 md:pb-20 px-9 md:px-[8%] lg:px-[15%]">
				<motion.p
					initial={{ opacity: 0, y: 12 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.4 }}
					className="text-[14px] uppercase tracking-[0.2em] text-white/30 mb-6"
				>
					Blog
				</motion.p>
				<motion.h1
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.5, delay: 0.05 }}
					className="text-[36px] md:text-[56px] lg:text-[72px] font-normal tracking-tight text-white leading-[1.05] max-w-3xl"
				>
					Thinking out loud.
				</motion.h1>
			</section>

			{/* Featured posts */}
			<section className="px-9 md:px-[8%] lg:px-[15%] pb-16 md:pb-20">
				<div className="grid md:grid-cols-2 gap-6">
					{featured.map((post, i) => (
						<motion.a
							key={post.title}
							href="#"
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true }}
							transition={{ duration: 0.4, delay: i * 0.08 }}
							className="group relative rounded-2xl bg-white/[0.03] border border-white/[0.06] p-8 md:p-10 hover:bg-white/[0.05] hover:border-white/[0.1] transition-all duration-300 flex flex-col justify-between min-h-[280px]"
						>
							<div>
								<div className="flex items-center gap-3 mb-6">
									<span className="text-[13px] text-white/25">{post.date}</span>
									<span className="w-1 h-1 rounded-full bg-white/15" />
									<span className="text-[13px] text-white/40">{post.tag}</span>
								</div>
								<h2 className="text-[22px] md:text-[26px] font-normal tracking-tight text-white leading-snug mb-4">{post.title}</h2>
								<p className="text-[15px] text-white/40 leading-relaxed">{post.excerpt}</p>
							</div>
							<div className="mt-8 flex items-center gap-2 text-white/30 group-hover:text-white/60 transition-colors">
								<span className="text-[14px]">Read</span>
								<ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
							</div>
						</motion.a>
					))}
				</div>
			</section>

			{/* All posts */}
			<section className="px-9 md:px-[8%] lg:px-[15%] pb-24 md:pb-32">
				<div className="space-y-0">
					{rest.map((post, i) => (
						<motion.a
							key={post.title}
							href="#"
							initial={{ opacity: 0, y: 12 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true }}
							transition={{ duration: 0.4, delay: i * 0.06 }}
							className="group border-t border-white/[0.06] py-8 md:py-10 grid md:grid-cols-[180px_1fr_40px] gap-4 md:gap-8 items-start"
						>
							<div className="flex items-center gap-3 md:flex-col md:items-start md:gap-2">
								<span className="text-[13px] text-white/25">{post.date}</span>
								<span className="text-[13px] text-white/40">{post.tag}</span>
							</div>
							<div>
								<h3 className="text-[18px] md:text-[20px] text-white font-normal tracking-tight group-hover:text-white/80 transition-colors mb-2">{post.title}</h3>
								<p className="text-[15px] text-white/35 leading-relaxed">{post.excerpt}</p>
							</div>
							<div className="hidden md:flex items-center justify-end pt-1">
								<ArrowUpRight size={16} className="text-white/20 group-hover:text-white/50 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
							</div>
						</motion.a>
					))}
				</div>
			</section>
		</PageLayout>
	);
}
